import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import PasswordInput from "@/components/PasswordInput";
import ThemeToggleFloating from "@/components/ThemeToggleFloating";
import { Zap, MailCheck } from "lucide-react";
import { ResendVerificationButton } from "@/components/ResendVerificationButton";

export default function RegisterPage() {
  const { register, registerTrial, error } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);

  // Proef is de standaardroute; de uitnodigingsroute alleen via ?invite=1, ?code=XYZ of /join-the-team.
  const isTrial = searchParams.get("trial") === "1"
    || (searchParams.get("invite") !== "1" && !searchParams.get("code") && window.location.pathname !== "/join-the-team");

  // Autofill invite code from ?code=XYZ deep-link (uppercased).
  useEffect(() => {
    const q = (searchParams.get("code") || "").trim().toUpperCase();
    if (q) setInviteCode(q);
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = isTrial
      ? await registerTrial(email, password, name, acceptTerms)
      : await register(email, password, name, inviteCode.trim().toUpperCase(), acceptTerms);
    setBusy(false);
    if (ok && ok.pending) { setPending(ok); return; }
    if (ok) nav("/");
  };

  if (pending) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background grid-bg relative">
        <ThemeToggleFloating />
        <div className="w-full max-w-md bg-card border-2 border-foreground p-8 heavy-shadow text-center" data-testid="register-pending">
          <MailCheck className="w-12 h-12 mx-auto text-primary" />
          <div className="stat-label mt-3">{t("Nog één stap")}</div>
          <h1 className="font-display font-black text-2xl uppercase mt-1">{t("Controleer je e-mail")}</h1>
          <p className="text-sm text-muted-foreground mt-3">
            {t("We hebben een bevestigingslink gestuurd naar")} <b className="font-mono-num text-foreground" data-testid="register-pending-email">{pending.email}</b>.
            {" "}{t("Klik erop om je account te activeren — daarna ben je direct ingelogd. De link is 24 uur geldig.")}
          </p>
          <p className="text-xs text-muted-foreground mt-3">{t("Geen mail? Kijk in je spamfolder of vraag hieronder een nieuwe aan.")}</p>
          <div className="mt-5 flex flex-col items-center gap-3">
            <ResendVerificationButton email={pending.email} compact />
            <Link to="/login" data-testid="register-pending-login" className="text-sm underline underline-offset-2">{t("Naar inloggen")}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background grid-bg relative">
      <ThemeToggleFloating className="top-4 right-4" />
      <form onSubmit={submit} className="w-full max-w-md bg-card border-2 border-foreground p-8 heavy-shadow" data-testid={isTrial ? "register-trial-form" : "register-invite-form"}>
        <div className="flex justify-center mb-6">
          <div className="bg-[#0b1735] p-3 border-2 border-foreground/20">
            <img
              src="/logo-meerjarenplan.png"
              alt="MeerjarenplanOnline.nl"
              className="h-8 w-auto"
              data-testid="brand-logo"
            />
          </div>
        </div>

        {isTrial ? (
          <>
            <div className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-2 py-1 mb-3 font-display uppercase font-bold text-xs tracking-widest" data-testid="register-trial-badge">
              <Zap className="w-3 h-3" />
              {t("14-daagse gratis proef")}
            </div>
            <h1 className="font-display font-black text-4xl uppercase tracking-tighter mb-2">{t("Start je proefperiode")}</h1>
            <p className="text-sm text-muted-foreground mb-6">
              {t("Geen creditcard. Je krijgt meteen een vooraf gevulde Demo Watersportclub om alle functies (inclusief Monte Carlo en PDF-export) uit te proberen.")}
            </p>
          </>
        ) : (
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter mb-6">{t("Account aanmaken")}</h1>
        )}

        {!isTrial && (
          <>
            <label className="stat-label">{t("Uitnodigingscode")}</label>
            <input
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              required
              maxLength={16}
              placeholder="ABC12XYZ"
              data-testid="register-invite-code"
              className="w-full mt-1 mb-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-muted-foreground mb-4">{t("Vraag je uitnodigingscode aan de beheerder.")}</p>
          </>
        )}

        <label className="stat-label">{t("Naam")}</label>
        <input
          value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name"
          className="w-full mt-1 mb-4 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <label className="stat-label">Email</label>
        <input
          type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email"
          className="w-full mt-1 mb-4 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <label className="stat-label">{t("Wachtwoord")}</label>
        <PasswordInput
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={isTrial ? 8 : 6}
          autoComplete="new-password"
          testId="register-password"
          className="mt-1 mb-4"
        />
        {isTrial && (
          <p className="text-xs text-muted-foreground -mt-3 mb-4">{t("Minimaal 8 tekens.")}</p>
        )}

        {error && <div data-testid="register-error" className="border-2 border-destructive bg-destructive/10 text-destructive p-3 text-sm mb-4">{error}</div>}

        <label className="flex items-start gap-3 mb-4 text-sm cursor-pointer select-none" data-testid="register-terms-label">
          <input
            type="checkbox"
            required
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            data-testid="register-accept-terms"
            className="mt-0.5 w-4 h-4 accent-primary shrink-0"
          />
          <span className="leading-snug">
            {t("Ik ga akkoord met de")}{" "}
            <Link to="/voorwaarden" target="_blank" rel="noopener noreferrer" data-testid="register-terms-link" className="underline font-semibold hover:text-primary">{t("algemene voorwaarden")}</Link>
            {" "}{t("en de")}{" "}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="register-privacy-link" className="underline font-semibold hover:text-primary">{t("privacyverklaring")}</Link>.
          </span>
        </label>

        <button disabled={busy || !acceptTerms} data-testid="register-submit"
          className="btn-brutal w-full bg-primary text-primary-foreground py-3 font-display uppercase font-black tracking-wider text-lg disabled:opacity-70">
          {busy
            ? t("Bezig met aanmaken…")
            : isTrial
              ? t("Proef starten")
              : t("Account aanmaken")}
        </button>

        <div className="mt-4 flex items-center justify-between flex-wrap gap-2 text-sm text-muted-foreground">
          <span>
            {t("Heb je al een account?")}{" "}
            <Link to="/login" data-testid="link-login" className="font-bold underline underline-offset-2 text-primary">{t("Inloggen")}</Link>
          </span>
          {isTrial ? (
            <Link to="/register?invite=1" data-testid="link-invite" className="text-xs underline underline-offset-2">{t("Heb je een uitnodigingscode?")}</Link>
          ) : (
            <Link to="/register?trial=1" data-testid="link-trial" className="text-xs underline underline-offset-2">{t("Gratis 14-dagen proef starten")}</Link>
          )}
        </div>
      </form>
    </div>
  );
}
